import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ImSpinner8 } from 'react-icons/im';
import { PayoutMethod } from '../types';

interface PaymentMethodsSelectorProps {
  selectedMethods: string[];
  onChange: (methods: string[]) => void;
  disabled?: boolean;
}

const PaymentMethodsSelector: React.FC<PaymentMethodsSelectorProps> = ({
  selectedMethods,
  onChange,
  disabled = false,
}) => {
  const [payoutMethodList, setPayoutMethodList] = useState<PayoutMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPayoutMethods();
  }, []);

  const fetchPayoutMethods = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/subaccount/payout-methods`
      );
      const methods = response.data.payoutmethods?.payload || [];
      setPayoutMethodList(methods);
    } catch (error) {
      console.error('Error fetching payout assets', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMethodChange = (asset: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedMethods, asset]);
    } else {
      onChange(selectedMethods.filter(a => a !== asset));
    }
  };

  const handleSelectAll = () => {
    if (selectedMethods.length === payoutMethodList.length) {
      onChange([]);
    } else {
      onChange(payoutMethodList.map(m => m.asset));
    }
  };

  // Separate fiat and crypto methods
  const fiatMethods = payoutMethodList.filter(m => !m.crypto);
  const cryptoMethods = payoutMethodList.filter(m => m.crypto);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-navy-700">
          Payout Assets
        </label>
        <div className="flex items-center justify-center py-12">
          <ImSpinner8
            className="spinner text-primary-500"
            style={{ fontSize: '1.8rem' }}
          />
        </div>
      </div>
    );
  }

  if (payoutMethodList.length === 0) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-navy-700">
          Payout Assets
        </label>
        <p className="text-sm text-gray-500">No payout assets available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-navy-700">
          Payout Assets
        </label>
        <button
          type="button"
          onClick={handleSelectAll}
          disabled={disabled}
          className="text-xs font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
        >
          {selectedMethods.length === payoutMethodList.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      {payoutMethodList.filter(m => !m.crypto).length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-navy-700">Fiat</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6">
            {payoutMethodList
              .filter(m => !m.crypto)
              .map(method => (
                <label
                  key={method.asset}
                  className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-all ${
                    selectedMethods.includes(method.asset)
                      ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedMethods.includes(method.asset)}
                      onChange={e => handleMethodChange(method.asset, e.target.checked)}
                      disabled={disabled}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-navy-700 truncate">
                        {method.asset}
                      </span>
                    </div>
                  </div>
                </label>
              ))}
          </div>
        </div>
      )}
      {payoutMethodList.filter(m => m.crypto).length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-navy-700">Crypto</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6">
            {payoutMethodList
              .filter(m => m.crypto)
              .map(method => (
                <label
                  key={method.asset}
                  className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-all ${
                    selectedMethods.includes(method.asset)
                      ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedMethods.includes(method.asset)}
                      onChange={e => handleMethodChange(method.asset, e.target.checked)}
                      disabled={disabled}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-navy-700 truncate">
                        {method.asset}
                      </span>
                    </div>
                  </div>
                </label>
              ))}
          </div>
        </div>
      )}

      {selectedMethods.length > 0 && (
        <p className="text-xs text-gray-500">
          {selectedMethods.length} payout asset{selectedMethods.length > 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );
};

export default PaymentMethodsSelector;
